/**
 * CrewEarningsGrid.jsx — Sierra-style editable spreadsheet of payout line items.
 *
 * Columns: Date · Item · Description · Price. One row = one thing you're owed.
 * The Price you type is the source of truth. Linking a job (🔗 picker in the Item
 * cell) prefills item + a suggested price from your split + a description — all
 * still editable. Rows persist to crew_earnings (server-scoped to the person).
 *
 * Owner owns the local row state; each row saves on blur (create if new, update
 * if it already has an id). Prefill saves immediately.
 */

import { useState, useEffect, useMemo, useRef, Fragment } from 'react';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { cn, fmtC } from '../../lib/utils';
import { useJobs } from '../../hooks/useJobs';
import { useTimeEntries } from '../../hooks/useTimeEntries';
import { useSettings } from '../../hooks/useSettings';
import { useRoster } from '../../hooks/useRoster';
import { useCrewEarnings, useCreateCrewEarning, useUpdateCrewEarning, useDeleteCrewEarning } from '../../hooks/useCrewEarnings';
import { computePrefillForPersonOnJob, getUnloggedHourlyGaps } from '../../lib/payoutEngine';
import { useToast } from '../layout/Toast';
import { logAction } from '../../lib/actionLogger';

const fmtISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
let TMP = 0;
const newDraft = (date) => ({ _key: `tmp_${++TMP}`, id: null, date, jobId: '', item: '', description: '', amount: '' });

// Default rate prefilled when a person has no stored rate (editable per row).
const DEFAULT_RATE = 30;

// Stable color per linked job → visual grouping of rows sharing a job.
const JOB_COLORS = ['#3B82F6', '#22c55e', '#D2691E', '#a855f7', '#ec4899', '#14b8a6', '#eab308', '#ef4444', '#0ea5e9', '#f97316'];
function jobColor(id) {
  if (!id) return null;
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return JOB_COLORS[h % JOB_COLORS.length];
}

export default function CrewEarningsGrid({ person }) {
  const { toast } = useToast();
  const { data: earnings = [] } = useCrewEarnings();
  const { data: jobs = [] } = useJobs();
  const { data: timeEntries = [] } = useTimeEntries();
  const { data: settings = { data: {} } } = useSettings();
  const { data: roster = [] } = useRoster();
  const create = useCreateCrewEarning();
  const update = useUpdateCrewEarning();
  const del = useDeleteCrewEarning();

  const today = useMemo(() => fmtISO(new Date()), []);

  // Local row state, seeded once per person from the person's UNPAID rows.
  const [rows, setRows] = useState([]);
  const rowsRef = useRef([]);
  rowsRef.current = rows;
  const seededRef = useRef(null);
  const savingKeys = useRef(new Set());
  const [syncingJob, setSyncingJob] = useState(null); // jobId being re-synced to its split

  useEffect(() => {
    if (!earnings) return;
    const mine = earnings
      .filter((e) => e.person === person && !e.paidAt)
      // Cluster rows that share a job together (unlinked rows last), then by date.
      .sort((a, b) => (a.jobId || '￿').localeCompare(b.jobId || '￿') || (a.date || '').localeCompare(b.date || ''))
      .map((e) => ({ _key: e.id, id: e.id, date: e.date || '', jobId: e.jobId || '', item: e.item || '', description: e.description || '', amount: e.amount != null ? String(e.amount) : '' }));
    if (seededRef.current === person) {
      // Already seeded this person. Re-seed ONLY on the cold-load case: the grid
      // is still a single pristine empty draft but real earnings have since loaded
      // (data arrived after first mount). Never clobber rows the user has touched.
      const cur = rowsRef.current;
      const pristine = cur.length === 1 && !cur[0].id && !cur[0].jobId && !(cur[0].item || '').trim() && !(cur[0].amount || '').trim();
      if (!(pristine && mine.length)) return;
    }
    seededRef.current = person;
    setRows(mine.length ? mine : [newDraft(today)]);
  }, [person, earnings, today]);

  // Jobs sorted: this person's jobs first — "mine" = board-assigned (assigned_crew)
  // OR named in the quote calcSnapshot (originator / lead / hourly).
  const sortedJobs = useMemo(() => {
    const lc = (person || '').toLowerCase();
    const active = jobs.filter((j) => j.status !== 'archived' && j.status !== 'void');
    const mine = [], rest = [];
    active.forEach((j) => {
      const names = new Set();
      const ac = j.assignedCrew || j.assigned_crew || [];
      (Array.isArray(ac) ? ac : []).forEach((n) => { names.add((typeof n === 'string' ? n : n?.name || '').toLowerCase()); });
      const s = j.calcSnapshot;
      if (s) {
        (s.originators || []).forEach((o) => { names.add((o.name || '').toLowerCase()); });
        ['prod', 'post'].forEach((d) => {
          const dd = s[d];
          if (!dd) return;
          const leads = dd.leads?.length ? dd.leads : (dd.lead ? [dd.lead] : []);
          leads.forEach((l) => { names.add((l.name || '').toLowerCase()); });
          (dd.hourly || []).forEach((h) => { names.add((h.name || '').toLowerCase()); });
        });
      }
      (names.has(lc) ? mine : rest).push(j);
    });
    return { mine, rest };
  }, [jobs, person]);

  // The person's hourly rate (admin-pool rate) — powers the Time → Price helper.
  const personRate = useMemo(() => {
    const m = roster.find((r) => (r.name || '').toLowerCase() === (person || '').toLowerCase());
    const cid = m?.crewId || m?.crew_id;
    return parseFloat(settings?.data?.crewRates?.[cid]?.admin) || 0;
  }, [roster, person, settings]);

  const hasContent = (r) => (parseFloat(r.amount) > 0) || (r.item || '').trim() || (r.description || '').trim();

  const persist = async (key, rowOverride) => {
    const row = rowOverride || rowsRef.current.find((r) => r._key === key);
    if (!row) return;
    if (savingKeys.current.has(key)) return;
    if (!row.id && !hasContent(row)) return;
    savingKeys.current.add(key);
    const payload = {
      person,
      date: row.date || null,
      jobId: row.jobId || null,
      item: (row.item || '').trim() || null,
      description: (row.description || '').trim() || null,
      amount: parseFloat(row.amount) || 0,
    };
    try {
      if (row.id) {
        const prev = earnings.find((pe) => pe.id === row.id);
        await update.mutateAsync({ id: row.id, ...payload });
        // Log only real content changes — persist fires on every field blur.
        const changed = !prev
          || (prev.date || null) !== payload.date
          || (prev.jobId || null) !== payload.jobId
          || (prev.item || null) !== payload.item
          || (prev.description || null) !== payload.description
          || (parseFloat(prev.amount) || 0) !== payload.amount;
        if (changed) {
          logAction({
            action: 'Edit Crew Earning',
            description: `Edited payout line — ${person} · ${payload.item || '—'} · ${fmtC(payload.amount)}`,
            details: { person, item: payload.item, amount: payload.amount, jobId: payload.jobId },
          });
        }
      } else {
        const created = await create.mutateAsync(payload);
        setRows((rs) => rs.map((r) => (r._key === key ? { ...r, id: created.id } : r)));
      }
    } catch (e) {
      toast(e?.message ? `Save failed: ${e.message}` : 'Save failed', 'error');
    } finally {
      savingKeys.current.delete(key);
    }
  };

  const updateCell = (key, field, val) => setRows((rs) => rs.map((r) => (
    r._key === key ? { ...r, [field]: val, ...(field === 'description' ? { _autoDesc: false } : {}) } : r
  )));

  // Time (h/m) × Rate → Price + a description. Rate defaults to the person's
  // stored rate but is editable per row. Purely a convenience; the row still
  // just stores amount + description.
  // Rate is always prefilled: the person's stored rate, else a default.
  const rateFallback = String(personRate > 0 ? personRate : DEFAULT_RATE);

  // Separate Hours + Minutes fields × Rate → Price. Does NOT touch the text field.
  const onHourlyField = (key, field, val) => {
    const row = rowsRef.current.find((r) => r._key === key);
    if (!row) return;
    const patch = { [field]: val };
    const h = field === '_h' ? val : (row._h ?? '');
    const m = field === '_m' ? val : (row._m ?? '');
    const rateStr = field === '_rate' ? val : (row._rate ?? rateFallback);
    const dec = (parseInt(h, 10) || 0) + (parseInt(m, 10) || 0) / 60;
    const rate = parseFloat(rateStr) || 0;
    if (dec > 0 && rate > 0) patch.amount = String(Math.round(dec * rate * 100) / 100);
    setRows((rs) => rs.map((r) => (r._key === key ? { ...r, ...patch } : r)));
  };

  const pickJob = (key, jobId) => {
    const row = rowsRef.current.find((r) => r._key === key);
    if (!row) return;
    const job = jobs.find((j) => j.id === jobId);
    // Prefill returns an array: legacy jobs → one aggregate row; items-mode
    // jobs → one row per item share (first fills this row, rest are appended)
    const pfRows = job ? computePrefillForPersonOnJob(job, person, timeEntries.filter((e) => e.jobId === jobId), settings, roster) : null;
    const first = pfRows?.[0] || null;
    const mergedItem = first
      ? `${first.item}${first.description ? ' — ' + first.description : ''}`
      : (job?.title || row.item);
    const updated = {
      ...row,
      jobId: jobId || '',
      item: mergedItem,
      description: null,
      amount: first ? String(first.amount) : row.amount,
    };
    setRows((rs) => rs.map((r) => (r._key === key ? updated : r)));
    persist(key, updated);
    (pfRows || []).slice(1).forEach((pf) => {
      const draft = {
        ...newDraft(row.date || today),
        jobId: jobId || '',
        item: `${pf.item}${pf.description ? ' — ' + pf.description : ''}`,
        amount: String(pf.amount),
      };
      setRows((rs) => [...rs, draft]);
      persist(draft._key, draft);
    });
  };

  const addRow = () => setRows((rs) => [...rs, newDraft(today)]);

  const removeRow = async (key) => {
    const row = rowsRef.current.find((r) => r._key === key);
    setRows((rs) => rs.filter((r) => r._key !== key));
    if (row?.id) {
      try {
        await del.mutateAsync(row.id);
        logAction({
          action: 'Delete Crew Earning',
          description: `Deleted payout line — ${person} · ${row.item || '—'} · ${fmtC(parseFloat(row.amount) || 0)}`,
          details: { person, item: row.item || null, amount: parseFloat(row.amount) || 0, jobId: row.jobId || null },
        });
      } catch (e) { toast('Delete failed', 'error'); }
    }
  };

  const total = rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);

  // Assign colors by order of first appearance so adjacent/distinct jobs never clash.
  const jobColorMap = useMemo(() => {
    const map = {}; let idx = 0;
    rows.forEach((r) => { if (r.jobId && !(r.jobId in map)) map[r.jobId] = JOB_COLORS[idx++ % JOB_COLORS.length]; });
    return map;
  }, [rows]);
  const colorFor = (id) => (id ? jobColorMap[id] || jobColor(id) : null);

  // ── Split drift ──────────────────────────────────────────────
  // For each linked job in the grid, compare what payroll currently shows
  // (sum of this person's unpaid rows for the job) against the job's CURRENT
  // computed split. If they differ, the split was re-run on the job page after
  // payroll was filled → surface a warning + Update on that job's group.
  const jobDrift = useMemo(() => {
    const byJob = {};
    rows.forEach((r) => {
      if (!r.jobId) return;
      if (!byJob[r.jobId]) byJob[r.jobId] = { stored: 0, current: 0, drift: false };
      byJob[r.jobId].stored += parseFloat(r.amount) || 0;
    });
    Object.keys(byJob).forEach((jobId) => {
      const job = jobs.find((j) => j.id === jobId);
      const pf = job ? computePrefillForPersonOnJob(job, person, timeEntries.filter((e) => e.jobId === jobId), settings, roster) : null;
      // No split for this person on this job (no snapshot / nothing earnable) → nothing to sync to; don't flag.
      if (!pf || !pf.length) return;
      const current = pf.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
      byJob[jobId].current = current;
      byJob[jobId].drift = Math.abs(current - byJob[jobId].stored) > 0.005;
    });
    return byJob;
  }, [rows, jobs, person, timeEntries, settings, roster]);

  // ── Unlogged hourly crew ─────────────────────────────────────
  // Hourly crew are paid for LOGGED time only, so a budgeted-but-unlogged hour
  // never reaches them — the item's lead earns share − hourlyTotal and quietly
  // absorbs it. Flag both sides of that on the job group: the lead is being
  // overpaid, the crew member is missing money until they log the time.
  const jobHourlyGaps = useMemo(() => {
    const lc = (person || '').toLowerCase();
    const byJob = {};
    [...new Set(rows.map((r) => r.jobId).filter(Boolean))].forEach((jobId) => {
      const job = jobs.find((j) => j.id === jobId);
      if (!job) return;
      const gaps = getUnloggedHourlyGaps(job, timeEntries.filter((e) => e.jobId === jobId));
      const absorbed = gaps.filter((g) => (g.absorbedBy || '').toLowerCase() === lc);
      const owed = gaps.filter((g) => (g.person || '').toLowerCase() === lc);
      if (absorbed.length || owed.length) byJob[jobId] = { absorbed, owed };
    });
    return byJob;
  }, [rows, jobs, person, timeEntries]);

  // Replace a whole job group with the job's current split (owner's choice:
  // wipe the job's unpaid rows, re-insert fresh prefill rows).
  const updateJobGroup = async (jobId) => {
    if (syncingJob) return;
    const job = jobs.find((j) => j.id === jobId);
    if (!job) return;
    const pf = computePrefillForPersonOnJob(job, person, timeEntries.filter((e) => e.jobId === jobId), settings, roster);
    if (!pf || !pf.length) return;
    setSyncingJob(jobId);
    const group = rowsRef.current.filter((r) => r.jobId === jobId);
    const keysToDrop = new Set(group.map((r) => r._key));
    const idsToDelete = group.filter((r) => r.id).map((r) => r.id);
    // Preserve the group's earliest date so the fresh rows keep their timeframe.
    const keepDate = group.map((r) => r.date).filter(Boolean).sort()[0] || today;
    const freshRows = pf.map((p) => ({
      ...newDraft(keepDate),
      jobId,
      item: `${p.item}${p.description ? ' — ' + p.description : ''}`,
      description: null,
      amount: String(p.amount),
    }));
    // Swap the group in place (keep its position in the list).
    setRows((rs) => {
      const out = []; let inserted = false;
      rs.forEach((r) => {
        if (keysToDrop.has(r._key)) { if (!inserted) { out.push(...freshRows); inserted = true; } }
        else out.push(r);
      });
      if (!inserted) out.push(...freshRows);
      return out;
    });
    const oldTotal = group.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
    const newTotal = pf.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
    try {
      await Promise.all(idsToDelete.map((id) => del.mutateAsync(id)));
      await Promise.all(freshRows.map((fr) => persist(fr._key, fr)));
      logAction({
        action: 'Sync Payroll to Job Split',
        description: `Synced ${person}'s payroll to the job split — ${job.title || jobId}: ${fmtC(oldTotal)} → ${fmtC(newTotal)}`,
        details: { person, jobId, jobTitle: job.title || null, oldTotal, newTotal, rows: freshRows.length },
      });
      toast('Payroll synced to the job split', 'success');
    } catch (e) {
      toast('Update failed', 'error');
    } finally {
      setSyncingJob(null);
    }
  };

  const noSpin = '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none';
  const cell = cn('w-full px-2 py-1.5 bg-transparent border border-transparent rounded text-sm text-text-primary hover:border-border focus:border-accent focus:bg-surface focus:outline-none', noSpin);
  // Roomy, boxed hours/minutes inputs
  const timeInput = cn('w-11 px-2 py-1.5 text-center text-sm bg-surface border border-border rounded text-text-primary focus:outline-none focus:border-accent', noSpin);

  return (
    <div className="card p-0 overflow-x-auto md:overflow-hidden">
      {/* Header */}
      <div className="grid grid-cols-[6.5rem_1fr_8.5rem_4.5rem_5.5rem_1.8rem] gap-1 px-2 py-1.5 bg-surface-2/60 border-b border-border text-2xs text-text-dim uppercase tracking-wider">
        <span>Date</span><span>Item / description</span><span>Time</span><span>Rate</span><span className="text-right">Price</span><span />
      </div>

      {/* Rows */}
      <div>
        {rows.map((r, i) => {
          const stripe = colorFor(r.jobId);
          const prevStripe = i > 0 ? colorFor(rows[i - 1].jobId) : null;
          const groupStart = stripe && stripe !== prevStripe; // first row of a job group
          const d = groupStart ? jobDrift[r.jobId] : null;
          const showDrift = !!(d && d.drift);
          const hg = groupStart ? jobHourlyGaps[r.jobId] : null;
          const showGaps = !!hg;
          const gap = groupStart && i > 0 ? '4px' : undefined;
          return (
          <Fragment key={r._key}>
          {showDrift && (
            <div
              className="flex items-center gap-2 px-2 py-1.5 border-b border-border/40 text-xs"
              style={{ borderLeft: `3px solid ${stripe}`, background: '#f59e0b14', marginTop: gap }}
            >
              <WarningAmberRoundedIcon sx={{ fontSize: 16, color: '#f59e0b' }} className="flex-shrink-0" />
              <span className="text-text-secondary min-w-0">
                Job split changed — payroll shows <b className="text-text-primary">{fmtC(d.stored)}</b>, job now splits to <b className="text-text-primary">{fmtC(d.current)}</b>
              </span>
              <button
                onClick={() => updateJobGroup(r.jobId)}
                disabled={syncingJob === r.jobId}
                className="ml-auto flex-shrink-0 px-2.5 py-1 rounded text-2xs font-semibold text-white disabled:opacity-50"
                style={{ background: '#f59e0b' }}
                title="Replace this job's payroll rows with its current split"
              >
                {syncingJob === r.jobId ? 'Updating…' : 'Update'}
              </button>
            </div>
          )}
          {showGaps && (
            <div
              className="px-2 py-1.5 border-b border-border/40 text-xs space-y-0.5"
              style={{ borderLeft: `3px solid ${stripe}`, background: '#f59e0b14', marginTop: showDrift ? undefined : gap }}
            >
              {hg.absorbed.length > 0 && (
                <div className="flex items-start gap-2">
                  <WarningAmberRoundedIcon sx={{ fontSize: 16, color: '#f59e0b' }} className="flex-shrink-0 mt-px" />
                  <span className="text-text-secondary min-w-0">
                    Includes <b className="text-text-primary">{fmtC(hg.absorbed.reduce((s, g) => s + g.amount, 0))}</b> of hourly crew budget these rows absorbed
                    {' — '}
                    {hg.absorbed.map((g) => `${g.person} (${g.unloggedHours}h on ${g.item})`).join(', ')}
                    {' '}hasn&apos;t logged the time yet.
                  </span>
                </div>
              )}
              {hg.owed.length > 0 && (
                <div className="flex items-start gap-2">
                  <WarningAmberRoundedIcon sx={{ fontSize: 16, color: '#f59e0b' }} className="flex-shrink-0 mt-px" />
                  <span className="text-text-secondary min-w-0">
                    <b className="text-text-primary">{fmtC(hg.owed.reduce((s, g) => s + g.amount, 0))}</b> of budgeted hourly work isn&apos;t here
                    {' — '}
                    {hg.owed.map((g) => `${g.unloggedHours}h on ${g.item}`).join(', ')}
                    {' '}must be logged before it&apos;s payable.
                  </span>
                </div>
              )}
            </div>
          )}
          <div
            className="grid grid-cols-[6.5rem_1fr_8.5rem_4.5rem_5.5rem_1.8rem] gap-1 px-2 py-0.5 items-center border-b border-border/40"
            style={{
              borderLeft: `3px solid ${stripe || 'transparent'}`,
              background: stripe ? `${stripe}12` : undefined,
              marginTop: showDrift || showGaps ? undefined : gap,
            }}
          >
            <input type="date" value={r.date} onChange={(e) => updateCell(r._key, 'date', e.target.value)} onBlur={() => persist(r._key)} className={cn(cell, 'text-xs')} />
            <div className="flex items-center gap-1 min-w-0">
              <input value={r.item} onChange={(e) => updateCell(r._key, 'item', e.target.value)} onBlur={() => persist(r._key)} placeholder="job, or what you did" className={cn(cell, 'flex-1 min-w-0')} />
              <div className="relative flex-shrink-0">
                <LinkRoundedIcon sx={{ fontSize: 15 }} style={stripe ? { color: stripe } : undefined} className={stripe ? '' : 'text-text-dim'} />
                <select value={r.jobId} onChange={(e) => pickJob(r._key, e.target.value)} title="Link an assigned job to prefill" className="absolute inset-0 opacity-0 cursor-pointer w-full">
                  <option value="">— no job —</option>
                  {sortedJobs.mine.length > 0 && (
                    <optgroup label="My Jobs">{sortedJobs.mine.map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}</optgroup>
                  )}
                  <optgroup label="Other Jobs">{sortedJobs.rest.map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}</optgroup>
                </select>
              </div>
            </div>
            <div className="flex items-center gap-1.5" title="Hours & minutes — Price = time × rate">
              <input type="number" min="0" value={r._h ?? ''} onChange={(e) => onHourlyField(r._key, '_h', e.target.value)} onBlur={() => persist(r._key)} placeholder="0" className={timeInput} />
              <span className="text-xs text-text-dim">h</span>
              <input type="number" min="0" max="59" value={r._m ?? ''} onChange={(e) => onHourlyField(r._key, '_m', e.target.value)} onBlur={() => persist(r._key)} placeholder="0" className={timeInput} />
              <span className="text-xs text-text-dim">m</span>
            </div>
            <div className="relative" title="Hourly rate for this row (edit as needed)">
              <span className="absolute left-1 top-1/2 -translate-y-1/2 text-text-dim text-2xs">$</span>
              <input type="number" min="0" step="0.01" value={r._rate ?? rateFallback} onChange={(e) => onHourlyField(r._key, '_rate', e.target.value)} onBlur={() => persist(r._key)} placeholder="/h" className={cn(cell, 'text-center pl-3 pr-0.5 text-xs')} />
            </div>
            <div className="relative">
              <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-text-dim text-xs">$</span>
              <input type="number" min="0" step="0.01" value={r.amount} onChange={(e) => updateCell(r._key, 'amount', e.target.value)} onBlur={() => persist(r._key)} placeholder="0" className={cn(cell, 'text-right pl-4 font-mono')} />
            </div>
            <button onClick={() => removeRow(r._key)} className="text-text-dim hover:text-red-accent flex justify-center" title="Delete row"><CloseRoundedIcon sx={{ fontSize: 16 }} /></button>
          </div>
          </Fragment>
          );
        })}
      </div>

      {/* Footer: add row + total */}
      <div className="flex items-center justify-between px-2 py-1.5 border-t border-border bg-surface-2/40">
        <button onClick={addRow} className="flex items-center gap-1 text-xs text-accent hover:underline"><AddRoundedIcon sx={{ fontSize: 16 }} /> Add row</button>
        <div className="text-sm">
          <span className="text-2xs text-text-dim uppercase tracking-wider mr-2">Total</span>
          <span className="font-heading text-green">{fmtC(total)}</span>
        </div>
      </div>
    </div>
  );
}
